
import React, { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Microscope, ArrowRight, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ResearchEmptyStateProps {
  isLoading: boolean;
  hasError: boolean;
  hasData: boolean;
  searchTerm?: string; // NEW: Add search term for context-aware messaging
}

export const ResearchEmptyState: React.FC<ResearchEmptyStateProps> = ({
  isLoading,
  hasError,
  hasData,
  searchTerm,
}) => {
  const navigate = useNavigate();

  // ✅ FIX: Move useState hook before early return to follow Rules of Hooks
  const [isAccordionMode, setIsAccordionMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024; // lg breakpoint
  });

  const navigateToGenerateResearch = useCallback(() => {
    // For private research, we can focus on the sidebar generation
    console.log('Navigate to generate private research');
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 1023px)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsAccordionMode(e.matches);
    };

    mql.addEventListener('change', handleChange);
    setIsAccordionMode(mql.matches);

    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Only show empty state when not loading, no error, and no data
  if (isLoading || hasError || hasData) {
    return null;
  }

  const ArrowIcon = isAccordionMode ? ArrowUp : ArrowRight;
  const actionText = isAccordionMode ? 'menu' : 'sidebar';

  return (
    <div className="space-y-2">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex flex-col items-center justify-center py-16 px-6 text-center"
      >
        {/* Beautiful icon with gradient background */}
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative w-20 h-20 mb-6 rounded-2xl bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 flex items-center justify-center shadow-lg border border-green-200/30 dark:border-green-700/30"
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 dark:from-green-400/20 dark:to-emerald-400/20"></div>
          <Microscope className="w-10 h-10 text-green-600 dark:text-green-400 relative z-10" />
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3] 
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut" 
            }}
            className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-400/20 to-emerald-400/20"
          />
        </motion.div>

        {/* Title */}
        <motion.h3 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3"
        >
          {searchTerm ? 'No matching research found' : 'Your Research'}
        </motion.h3>

        {/* Description */}
        <motion.p 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-gray-600 dark:text-gray-300 mb-8 max-w-lg leading-relaxed"
        >
          {searchTerm 
            ? `No research found matching "${searchTerm}". Try adjusting your search terms or generate new research with the AI generator.`
            : 'From zero to PhD-level knowledge in minutes. Build your personal research empire without breaking a sweat.'
          }
        </motion.p>

        {/* Call to action with arrow - ✅ CHANGED: Made text white for better readability */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex items-center gap-3 px-6 py-3 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200/40 dark:border-green-700/40"
        >
          <span className="text-sm font-medium text-white">
            Use the Search & Create {actionText} to start
          </span>
          <motion.div
            animate={isAccordionMode ? { 
              y: [0, -3, 0],
            } : {
              x: [0, 3, 0],
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut" 
            }}
          >
            <ArrowIcon className="w-4 h-4 text-white" />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResearchEmptyState;
